import { environment } from '@core/environment';
import { useAppDispatch } from '@core/redux/hooks';
import { sortAscByCreatedAt } from '@core/logic';
import { AppDispatch } from '@core/redux/store';
import {
  ChatBubbleLinesOutline,
  ChipOutline,
  CubeMesh,
  CircleInformationOutline,
} from '@deepup/icons';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ProcessingIndicator } from '@processingResults/components/ProcessingIndicator';
import { processingResultSelectors } from '@processingResults/redux';
import { ProcessingResult } from '@processingResults/types';
import { postTransformScan } from '@scans/components/Scans/helper';
import { usePermissions } from '@userProfile/hooks';
import { viewer3dSelectors } from '@viewer3D/redux';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { proxy, useSnapshot } from 'valtio';

export enum TransformationReason {
  Unspecified = 'UNSPECIFIED',
  AutoAligned = 'AUTO_ALIGNED',
  AlignedToOtherScans = 'ALIGNED_TO_OTHER_SCANS',
  AlignedToPhotos = 'ALIGNED_TO_PHOTOS',
  AlignedToSatelliteOrMap = 'ALIGNED_TO_SATELLITE_OR_MAP',
  CorrectedPositionData = 'CORRECTED_POSITION_DATA',
  CustomerRequest = 'CUSTOMER_REQUEST',
  Custom = 'CUSTOM',
}

interface TransformationReasonDialogState {
  showTransformationReasonDialog: {
    processingResult: ProcessingResult | null;
    preventRepeatedOpening: boolean;
    open: boolean;
    onSubmit: (reason: TransformationReason, comment: string) => void;
  };
}

const transformationReasonDialogState = proxy<TransformationReasonDialogState>({
  showTransformationReasonDialog: {
    processingResult: null,
    preventRepeatedOpening: false,
    open: false,
    onSubmit: () => {},
  },
});

const closeTransformationReasonDialog = () => {
  transformationReasonDialogState.showTransformationReasonDialog = {
    processingResult: null,
    preventRepeatedOpening: false,
    open: false,
    onSubmit: () => {},
  };
};

const setTransformationReasonDialog = (
  open: boolean,
  processingResult: ProcessingResult | null,
  preventRepeatedOpening: boolean,
  onSubmit: (reason: TransformationReason, comment: string) => void,
): void => {
  transformationReasonDialogState.showTransformationReasonDialog = {
    processingResult,
    preventRepeatedOpening,
    open: open,
    onSubmit,
  };
};

export const useTransformationReasonDialogState = () => ({
  ...useSnapshot(transformationReasonDialogState),
  setTransformationReasonDialog,
});

const submitTransformationReason = async (
  dispatch: AppDispatch,
  projectId: string,
  processingResult: ProcessingResult,
  reason: TransformationReason,
  comment: string,
) => {
  await postTransformScan(dispatch, processingResult.scanId, projectId, processingResult.id, {
    processingResult: {
      ...processingResult,
      reason,
      comment,
    },
    reason,
    comment,
  });
};

export const TransformationReasonDialog = () => {
  const { showTransformationReasonDialog } = useTransformationReasonDialogState();

  const selectedScan = useSelector(viewer3dSelectors.selectedScan);
  const selectedProcessingResult = useSelector(
    processingResultSelectors.selectById(selectedScan?.selectedProcessingResultId),
  );
  const processingResult =
    showTransformationReasonDialog.processingResult || selectedProcessingResult;

  const [transformationReason, setTransformationReason] = useState<
    TransformationReason | undefined
  >(
    processingResult?.reason === TransformationReason.AutoAligned
      ? undefined
      : processingResult?.reason,
  );

  const [transformationComment, setTransformationComment] = useState<string>(
    processingResult?.reason === TransformationReason.AutoAligned
      ? ''
      : processingResult?.comment || '',
  );

  useEffect(() => {
    if (
      showTransformationReasonDialog.open &&
      processingResult?.reason &&
      processingResult.reason !== TransformationReason.Unspecified &&
      processingResult.reason !== TransformationReason.AutoAligned &&
      showTransformationReasonDialog.preventRepeatedOpening
    ) {
      showTransformationReasonDialog.onSubmit(
        processingResult.reason,
        processingResult.comment || '',
      );
      closeTransformationReasonDialog();
    }
  }, [
    showTransformationReasonDialog.open,
    processingResult?.reason,
    processingResult?.comment,
    showTransformationReasonDialog.preventRepeatedOpening,
  ]);

  useEffect(() => {
    // If the current reason is AUTO_ALIGNED, clear it to force the user to select a new reason
    if (processingResult?.reason === TransformationReason.AutoAligned) {
      setTransformationReason(undefined);
      setTransformationComment('');
    } else {
      setTransformationReason(processingResult?.reason);
      setTransformationComment(processingResult?.comment || '');
    }
  }, [processingResult]);

  const handleCommentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTransformationComment(event.target.value);
  };

  return (
    <Dialog open={showTransformationReasonDialog.open} onClose={closeTransformationReasonDialog}>
      <DialogTitle>Transformation Reason</DialogTitle>
      <DialogContent>
        <RadioGroup
          value={transformationReason}
          onChange={(e) => setTransformationReason(e.target.value as TransformationReason)}
        >
          {Object.keys(TransformationReason)
            .filter((reasonKey) => reasonKey !== 'Unspecified' && reasonKey !== 'AutoAligned')
            .map((reasonKey) => (
              <FormControlLabel
                key={reasonKey}
                value={TransformationReason[reasonKey as keyof typeof TransformationReason]}
                control={<Radio />}
                label={reasonKey.replace(/([A-Z])/g, ' $1').trim()}
              />
            ))}
        </RadioGroup>
        <TextField
          id="transformation-comment"
          label="Comment"
          variant="outlined"
          defaultValue={transformationComment}
          onChange={handleCommentChange}
          required={transformationReason === TransformationReason.Custom}
        />
      </DialogContent>
      <DialogActions>
        <Button color="secondary" variant="outlined" onClick={closeTransformationReasonDialog}>
          Cancel
        </Button>
        <Button
          autoFocus
          variant="contained"
          disabled={
            !transformationReason ||
            (transformationReason === TransformationReason.Custom && !transformationComment) ||
            transformationReason === TransformationReason.Unspecified
          }
          onClick={() => {
            showTransformationReasonDialog.onSubmit(
              transformationReason || TransformationReason.Unspecified,
              transformationComment,
            );
            closeTransformationReasonDialog();
          }}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const ProcessingResultsList = ({
  selectedProcessingResultId,
  processingResults,
  onSelectResult,
}: {
  onSelectResult: (result: ProcessingResult) => void;
  processingResults: ProcessingResult[];
  selectedProcessingResultId?: string;
}) => {
  const permissions = usePermissions();
  const dispatch = useAppDispatch();
  const projectId = useSelector(viewer3dSelectors.selectedProjectId)!;
  const project = useSelector(viewer3dSelectors.selectedProject)!;

  const { setTransformationReasonDialog } = useTransformationReasonDialogState();

  const showDialog = (processingResult: ProcessingResult) => {
    setTransformationReasonDialog(true, processingResult, false, (reason, comment) => {
      submitTransformationReason(dispatch, projectId, processingResult, reason, comment);
    });
  };

  if (!selectedProcessingResultId || !processingResults.length) {
    return <Typography variant="subtitle2">No processing results available</Typography>;
  }

  return (
    <>
      <RadioGroup value={selectedProcessingResultId}>
        {[...processingResults].sort(sortAscByCreatedAt).map((result) => (
          <Stack direction="column" key={result.id}>
            <Stack direction="row" alignItems="center">
              <FormControlLabel
                value={result.id}
                onClick={() => onSelectResult(result)}
                control={<Radio />}
                label={
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Typography variant="subtitle2">
                      {DateTime.fromISO(result.createdAt).toLocaleString(
                        DateTime.DATETIME_MED_WITH_WEEKDAY,
                      )}
                    </Typography>
                    <ProcessingIndicator processingResultId={result.id} size={16} />
                    <Tooltip title="Go to HD-Mesh">
                      <IconButton
                        color="secondary"
                        href={`${environment.trenchviewerFrontendUrl}/hd-mesh/${result.scanId}`}
                        target="_blank"
                        role="link"
                        size="small"
                      >
                        <CubeMesh />
                      </IconButton>
                    </Tooltip>
                    {permissions.canViewScanJobs && (
                      <Tooltip title="Inspect Scan processing status">
                        <IconButton
                          color="secondary"
                          href={`${environment.scanDashboardUrl}${result.scanId}`}
                          target="_blank"
                          role="link"
                          aria-label="Job"
                          size="small"
                        >
                          <ChipOutline />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                }
                sx={{ marginRight: '0.5rem' }}
              />
              <Tooltip
                title={
                  result.reason === TransformationReason.AutoAligned
                    ? 'Auto-aligned transformations cannot be edited'
                    : 'Add or edit comment'
                }
              >
                <span>
                  <IconButton
                    color="secondary"
                    role="button"
                    aria-label="Job"
                    size="small"
                    disabled={result.reason === TransformationReason.AutoAligned}
                    onClick={() => {
                      const processingResult = processingResults.find((pr) => pr.id === result.id);
                      if (!processingResult) return;
                      showDialog(processingResult);
                    }}
                  >
                    <ChatBubbleLinesOutline />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
            {result.texturedMeshes?.length > 0 &&
            result.texturedMeshes[0]?.mesh?.zone?.value !== project.utmZone ? (
              <FormHelperText error>
                UTM zone mismatch
                <Tooltip
                  title={`The UTM zone of the processing result (${result.texturedMeshes[0].mesh.zone.value}) does not match the UTM zone of the project (${project.utmZone}). Please contact your manager.`}
                >
                  <IconButton color="error" role="span" aria-label="UTM mismatch" size="small">
                    <CircleInformationOutline />
                  </IconButton>
                </Tooltip>
              </FormHelperText>
            ) : null}
          </Stack>
        ))}
      </RadioGroup>
    </>
  );
};
